import { useTheme } from "@mui/material";
import { Search20Px } from "@qubit/autoparts";
import { parse, stringify } from "qs";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { setSelectedSummaries } from "~/features/inventory/inventory.slice";
import { useConfigurePortsWithRetry } from "~/hooks/useConfigurePortsWithRetry";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import {
  selectThisWorkstation,
  selectWorkstationHasInventoryRole
} from "~/redux/selectors/workstationsSelectors";

import { MainButton } from "./MainButton";
import { PreparingWorkstationModal } from "./PreparingWorkstationModal";

export type Props = {
  route: string;
};
export function InventoryButton({ route }: Props) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const navigate = useFcNavigate();
  const dispatch = useAppDispatch();
  const { search }: { search: string } = useLocation();
  const trackedPageName = useMixpanelPageName();

  const isInventoryRoleFound = useAppSelector(
    selectWorkstationHasInventoryRole
  );

  const { configurePortsWithRetry, isConfigurePortsRetrying } =
    useConfigurePortsWithRetry({
      workstationMode: "Problem Solving"
    });

  const hasWorkstation = !!useAppSelector(selectThisWorkstation);

  const onInventoryButtonClicked = async () => {
    if (isInventoryRoleFound || !hasWorkstation) {
      const isConfigurePortsSuccess = await configurePortsWithRetry();
      if (!isConfigurePortsSuccess) {
        return;
      }

      navigate({
        pathname: route,
        search: stringify({
          ...parse(search, { ignoreQueryPrefix: true }),
          autostore: true
        })
      });
      mixpanelTrack({
        type: "Button Click",
        label: "Autostore Inventory Button",
        trackedPageName
      });
      dispatch(setSelectedSummaries([]));
    }
  };

  const alert =
    (!hasWorkstation && t("no workstation selected")) ||
    (!isInventoryRoleFound && hasWorkstation && t("no inventory role found")) ||
    undefined;

  const alertKind =
    (!hasWorkstation && "workstation") ||
    (!isInventoryRoleFound && hasWorkstation && "workstation") ||
    undefined;

  return (
    <>
      <MainButton
        alert={alert}
        alertKind={alertKind}
        onClick={onInventoryButtonClicked}
        variant="md"
        disabled={!isInventoryRoleFound && hasWorkstation}
        icon={
          <Search20Px
            width={30}
            height={30}
            fill={palette.primary.contrastText}
          />
        }
      >
        {t("nav.link.inventory")}
      </MainButton>

      <PreparingWorkstationModal isOpen={isConfigurePortsRetrying} />
    </>
  );
}
